import { useEffect, useMemo, useState, useRef } from "react";
import SearchInput from "./shared/SearchInput";
import HelpButton from "./shared/HelpButton";
import { fetchPeriodical, fetchTkEmailCandidates, fetchTkCcDefault, saveTkCcDefault,
  pdReportPreview, pdReportSend } from "../utils/api";


const MONTHS3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];


const FIELD_KEYS = [
  "Periodical", "Start Date", "End Date", "Site", "Quote1", "Cleaners1",
  "Rework1", "Expense Chemical1", "Expense Equipment1", "Expense Incidental1",
  "SNL Payment", "Cleaner Payment", "Cleaner Attending", "Rate1", "Day1",
  "Percent1", "PercentRate1", "Work1", "Cycle-Type1", "Rollover", "Work Status", "Planes",
];
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[$,\s]/g, "")); return Number.isFinite(n) ? n : 0; };
const toObjects = (arrs) => (arrs || []).map((a) => {
  const o = { id: a[0] };
  FIELD_KEYS.forEach((k, i) => { o[k] = a[i + 1]; });
  o.quote = num(o["Quote1"]);
  o.cleaners = num(o["Cleaners1"]);
  o.snl = num(o["SNL Payment"]);
  o.payEach = num(o["Cleaner Payment"]);
  o.payTotal = o.payEach * (o.cleaners || 1);      // per-cleaner × cleaners
  o.days = num(o["Day1"]);
  o.start = String(o["Start Date"] || "").slice(0, 10);   // ISO from Airtable
  o.end = String(o["End Date"] || "").slice(0, 10);
  return o;
});

const fmtDate = (iso) => {
  if (!iso || !iso.includes("-")) return iso || "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};


const DEF_NAME = "Cleaner Name"; 
const eqI = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
const isUnclaimed = (r) => !String(r["Cleaner Attending"] || "").trim() || eqI(r["Cleaner Attending"], DEF_NAME);
const isClaimed = (r) => !isUnclaimed(r);
const isCompleted = (r) => eqI(r["Work Status"], "Completed");
const isActive = (r) => eqI(r["Work Status"], "Pending") || eqI(r["Work Status"], "In Progress");
const startsFuture = (r) => r.start && r.start > todayISO();
const startsToday = (r) => r.start === todayISO();
const isABN = (r) => eqI(r["Work1"], "ABN");
const isTFN = (r) => eqI(r["Work1"], "TFN");


const ico = { width:14, height:14, flexShrink:0, color:"rgba(255,255,255,0.45)" };
const CalIcon = () => (
  <svg style={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const JobIcon = () => (
  <svg style={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const SiteIcon = () => (
  <svg style={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

/* status colours — Pending red · In Progress blue · Completed green */
const statusColor = (s) =>
  eqI(s, "Completed") ? "#4ade80" : eqI(s, "In Progress") ? "#60a5fa" : eqI(s, "Pending") ? "#f87171" : "#94a3b8";


const FILTERS = [
  /* row 1 — claim pipeline */
  { key:"unclaimed", label:"Unclaimed",            tone:"grey",  test:(r) => isUnclaimed(r) },
  { key:"active",    label:"Claimed · Active",     tone:"amber", test:(r) => isClaimed(r) && isActive(r) },
  { key:"done",      label:"Claimed · Completed",  tone:"green", test:(r) => isClaimed(r) && isCompleted(r) },
  { key:"advance",   label:"Claimed in Advance",   tone:"amber", test:(r) => isClaimed(r) && startsFuture(r) && !isCompleted(r),
    sub:startsToday },
  /* row 2 — counts */
  { key:"zeroq",     label:"Zero Quote",           tone:"red",   test:(r) => r.quote === 0 },
  { key:"quoted",    label:"Quoted",               tone:"green", test:(r) => r.quote > 0 },
  { key:"abn",       label:"Contractor",           tone:"cyan",  test:isABN },
  { key:"tfn",       label:"Employee",             tone:"cyan",  test:isTFN },
  /* row 3 — $ sums */
  { key:"payzero",   label:"Cleaners Pay · Zero Quote", tone:"red",   money:true, test:(r) => r.quote === 0, sum:(r) => r.payTotal },
  { key:"snlq",      label:"SNL Pay · Quoted",          tone:"green", money:true, test:(r) => r.quote > 0,   sum:(r) => r.snl },
  { key:"abn$",      label:"Contractor Pay",            tone:"cyan",  money:true, test:isABN,                sum:(r) => r.payTotal },
  { key:"tfn$",      label:"Employee Pay",              tone:"cyan",  money:true, test:isTFN,                sum:(r) => r.payTotal },
];
const ROWS_OF_FILTERS = [FILTERS.slice(0, 4), FILTERS.slice(4, 8), FILTERS.slice(8)];

const toneColor = (t) =>
  t === "red" ? "#f87171" : t === "green" ? "#4ade80" : t === "amber" ? "#fbbf24" : t === "cyan" ? "#22d3ee" : "#94a3b8";

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:1200,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  nav: { display:"flex", alignItems:"center", justifyContent:"center", gap:14, margin:"2px 0 10px" },
  navBtn: { width:32, height:32, borderRadius:9, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.95rem", lineHeight:1 },
  navLabel: { minWidth:120, textAlign:"center", fontWeight:800, color:"#f1f5f9", fontSize:"0.95rem", letterSpacing:"0.02em" },
  chipsRow: { display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0" },
  dayChip: (on) => ({ padding:"4px 12px", borderRadius:999, fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
    border:`1px solid ${on ? "rgba(59,130,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.03)",
    color: on ? "#93c5fd" : "rgba(255,255,255,0.55)" }),
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:10, margin:"10px 0 6px" },
  countBtn: (active) => ({ borderRadius:12, padding:"11px 8px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.62rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (tone, money) => ({ fontSize: money ? "1.1rem" : "1.55rem", fontWeight:800, lineHeight:1.2, color: toneColor(tone), whiteSpace:"nowrap" }),
  countSub: { fontSize:"0.6rem", color:"rgba(255,255,255,0.45)", fontWeight:700 },
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:8 },
  dateHead: { display:"flex", justifyContent:"space-between", alignItems:"baseline",
    padding:"10px 8px 6px", position:"sticky", top:0, background:"#1b2537", zIndex:1 },
  dateTitle: { fontSize:"0.78rem", fontWeight:800, color:"#22d3ee", letterSpacing:"0.04em" },
  dateMeta: { fontSize:"0.7rem", color:"rgba(255,255,255,0.45)" },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  iconCell: { display:"flex", alignItems:"center", gap:7, minWidth:0 },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  dim: { fontSize:"0.82rem", color:"rgba(255,255,255,0.35)", fontStyle:"italic",
    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  periodical: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  badge: (color) => ({ fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.03em",
    padding:"1px 7px", borderRadius:999, whiteSpace:"nowrap",
    background:`${color}1f`, color, border:`1px solid ${color}66` }),
  group: { border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, padding:"8px 8px 8px", marginBottom:14,
    background:"rgba(255,255,255,0.02)" },
  detail: { display:"grid", gridTemplateColumns:"120px 120px 120px minmax(220px,2fr) 50px 1fr 130px 1fr", columnGap:10,
    padding:"8px 10px 10px", margin:"4px 0 0", borderRadius:8,
    background:"rgba(34,211,238,0.05)", border:"1px solid rgba(34,211,238,0.14)" },
  detailLabel: { display:"block", fontSize:"0.58rem", letterSpacing:"0.06em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.4)", marginBottom:2, whiteSpace:"nowrap" },
  detailValue: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
};

const TABLE_COLS = "1fr 2fr 1fr auto";


const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:640, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"16px 22px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  hTitle: { fontSize:"1rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"8px 22px 4px", maxHeight:"min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY:"auto" },
  footer: { padding:"14px 22px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: (busy) => ({ background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 20px", fontSize:"0.85rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, display:"inline-flex", alignItems:"center", gap:8 }),
  row: { display:"grid", gridTemplateColumns:"28px 1.2fr 1fr 1.4fr", alignItems:"center",
    columnGap:8, padding:"9px 4px", borderBottom:"1px solid rgba(255,255,255,0.06)" },
  rName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  rCell: { fontSize:"0.8rem", color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  pill: { fontSize:"0.72rem", color:"rgba(255,255,255,0.55)" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  note: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80" },
  err: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spin = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"pd-spin 0.7s linear infinite" }} />
);


const SendModal = ({ filterHeader, reportRows, summary, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState("");             // "preview" | "send"
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const CC_CATEGORIES = [
    { key: "supervisor", label: "Supervisors" },
    { key: "admin",      label: "Admin" },
    { key: "operations", label: "Operations" },
  ];
  const catOf = (r) => r.category || "operations";
  const [chips, setChips] = useState([]);

  useEffect(() => {
    Promise.all([fetchTkEmailCandidates(), fetchTkCcDefault()]).then(([cand, def]) => {
      const candidates = cand.status === "success" ? cand.candidates || [] : [];
      const active = def.status === "success" && Array.isArray(def.chips) ? def.chips : ["supervisor"];
      setList(candidates);
      setChips(active);
      const pre = {};
      candidates.forEach((r) => { if (active.includes(catOf(r))) pre[r.email] = true; });
      setChecked(pre);
      setLoading(false);
    });
  }, []);

  const toggleChip = (key) => {
    const next = chips.includes(key) ? chips.filter((c) => c !== key) : [...chips, key];
    setChips(next);
    const pre = {};
    list.forEach((r) => { if (next.includes(catOf(r))) pre[r.email] = true; });
    setChecked(pre);
    saveTkCcDefault(next);
  };
  const allChecked = list.length > 0 && list.every((r) => checked[r.email]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) list.forEach((r) => { next[r.email] = true; });
    setChecked(next);
  };
  const recipients = list.filter((r) => checked[r.email]).map((r) => ({ name: r.name, email: r.email }));

  const preview = async () => {
    setBusy("preview"); setError(""); setNote("");
    const res = await pdReportPreview(filterHeader, reportRows, summary);
    if (res.status === "success") window.open(URL.createObjectURL(res.blob), "_blank");
    else setError(res.error || "Preview failed");
    setBusy("");
  };
  const send = async () => {
    setBusy("send"); setError(""); setNote("");
    const res = await pdReportSend(filterHeader, reportRows, recipients, summary);
    if (res.status === "success") {
      setNote(`Sent — ${recipients.length} personalised email${recipients.length === 1 ? "" : "s"} + work inbox copy.`);
    } else setError(res.error || "Send failed");
    setBusy("");
  };

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={O.modal}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>✉ Send Periodical Report</h5>
          <span style={O.pill}>{reportRows.length} periodicals in filter</span>
        </div>
        <div style={O.body}>
          <div style={{ ...O.pill, margin:"2px 0 8px" }}>{filterHeader}</div>
          <div style={{ display:"flex", gap:6, margin:"4px 0 10px" }}>
            {CC_CATEGORIES.map((c) => (
              <button key={c.key} type="button" style={S.dayChip(chips.includes(c.key))} onClick={() => toggleChip(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          {note && <div style={O.note}>{note}</div>}
          {error && <div style={O.err}>{error}</div>}
          <div style={{ ...O.row, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            <input type="checkbox" style={O.check} checked={allChecked} onChange={toggleAll} title="Select all" />
            {["Name", "Job", "Email"].map((h) => (
              <span key={h} style={{ fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:"0.05em",
                color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,0.5)" }}>Loading…</div>
          ) : list.map((r) => (
            <div key={r.email} style={O.row}>
              <input type="checkbox" style={O.check} checked={!!checked[r.email]}
                onChange={() => setChecked((p) => ({ ...p, [r.email]: !p[r.email] }))} />
              <span style={O.rName}>{r.name}</span>
              <span style={O.rCell}>{r.job || "—"}</span>
              <span style={O.rCell}>{r.email}</span>
            </div>
          ))}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Close</button>
          <button type="button" style={O.confirm(busy === "preview")} onClick={preview} disabled={!!busy || reportRows.length === 0}>
            {busy === "preview" ? <Spin /> : "👁"} Preview Report
          </button>
          <button type="button" style={O.confirm(busy === "send")} onClick={send} disabled={!!busy || reportRows.length === 0}>
            {busy === "send" ? <Spin /> : "✉"} Send Emails ({recipients.length})
          </button>
        </div>
      </div>
    </div>
  );
};


const PeriodicalRow = ({ r }) => {
  const sc = statusColor(r["Work Status"]);
  return (
    <div style={S.group}>
      <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center", columnGap:10, padding:"2px 0 4px" }}>
        <span style={S.iconCell}><CalIcon /><span style={S.cell}>{startsToday(r) ? "⚡ " : ""}{fmtDate(r.start)} → {fmtDate(r.end)}</span></span>
        <span style={S.iconCell}><JobIcon /><span style={S.periodical} title={r["Periodical"]}>{r["Periodical"] || "—"}</span></span>
        <span style={S.iconCell}><SiteIcon /><span style={S.name} title={r["Site"]}>{r["Site"] || "—"}</span></span>
        <span style={S.badge(sc)}>{r["Work Status"] || "Pending"}</span>
      </div>
      <div style={S.detail}>
        <span><span style={S.detailLabel}>Quote</span>
          <span style={{ ...S.detailValue, color: r.quote > 0 ? "#4ade80" : "#f87171", fontWeight:700 }}>{fmt$(r.quote)}</span></span>
        <span><span style={S.detailLabel}>SNL Payment</span>
          <span style={{ ...S.detailValue, color: r.snl >= 0 ? "#4ade80" : "#f87171", fontWeight:700 }}>{fmt$(r.snl)}</span></span>
        <span><span style={S.detailLabel}>Cleaners Payment</span>
          <span style={{ ...S.detailValue, color:"#fbbf24", fontWeight:700 }}>{fmt$(r.payTotal)}</span>
        </span>
        <span><span style={S.detailLabel}>Cleaners Attending</span>
          {isClaimed(r) ? <span style={{ ...S.detailValue, color: sc, fontWeight:700 }}>{r["Cleaner Attending"]}</span>
                        : <span style={S.dim}>Unclaimed</span>}
        </span>
        <span><span style={S.detailLabel}>Days</span><span style={S.detailValue}>{r.days || "—"}</span></span>
        <span><span style={S.detailLabel}>Cycle</span><span style={S.detailValue}>{r["Cycle-Type1"] || "—"}</span></span>
        <span><span style={S.detailLabel}>Cleaners</span>
          <span style={{ ...S.detailValue, fontWeight:700 }}>{r.cleaners || "—"}</span>
          {r.cleaners > 0 && <span style={{ ...S.detailValue, fontSize:"0.68rem", color:"rgba(255,255,255,0.45)" }}> × {fmt$(r.payEach)}</span>}
        </span>
        <span><span style={S.detailLabel}>Rollover</span><span style={S.detailValue}>{r["Rollover"] || "—"}</span></span>
      </div>
    </div>
  );
};


const PeriodicalDashboard = ({ onClose, refreshKey = 0, onBusy }) => {
  const inline = !onClose;                    // rendered inside a tab, not as a modal
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);
  const [sameStartOnly, setSameStartOnly] = useState(false);
  const [filters, setFilters] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [dates, setDates] = useState([]);
  const dateRef = useRef(null);
  const monthYear = `${MONTHS3[month]}${year}`;
  const mm = String(month + 1).padStart(2, "0");
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateMin = `${year}-${mm}-01`;
  const dateMax = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchPeriodical({ monthYear });
        if (alive) setRows(res.status === "success" ? toObjects(res.periodicals) : []);
      } catch { if (alive) setRows([]); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [monthYear]);

  
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      const res = await fetchPeriodical({ monthYear });
      if (res.status === "success") setRows(toObjects(res.periodicals));
    } catch { /* keep current rows */ }
    setRefreshing(false);
  };
  useEffect(() => { if (refreshKey) refresh(); }, [refreshKey]);
  useEffect(() => { onBusy?.(loading || refreshing); }, [loading, refreshing, onBusy]);

  useEffect(() => {
    if (inline) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, inline]);

  const nav = (d) => {
    let m = month + d, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setFilters([]); setDates([]);
  };

  
  const advancePreset = () => {
    setFilters(["advance"]);
    setDates([]); setTodayOnly(false); setSameStartOnly(false); setQuery("");
  };

  
  const scoped = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    return rows.filter((r) => {
      if (dates.length && !dates.includes(r.start)) return false;
      if (todayOnly && !startsToday(r)) return false;
      if (q) {
        const hay = `${r["Site"]}${r["Periodical"]}${r["Work Status"]}${r["Work1"]}${r["Cleaner Attending"]}`
          .toLowerCase().replace(/\s+/g, "");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, dates, todayOnly, query]);

  const counts = useMemo(() => {
    const c = {}, sub = {}, money = {};
    FILTERS.forEach((f) => {
      const hit = scoped.filter(f.test);
      c[f.key] = hit.length;
      if (f.sub) sub[f.key] = hit.filter(f.sub).length;
      if (f.money) money[f.key] = hit.reduce((s, r) => s + f.sum(r), 0);
    });
    return { c, sub, money };
  }, [scoped]);

  const filtered = useMemo(() => {
    if (!filters.length) return scoped;
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return scoped.filter((r) => active.some((f) => f.test(r)));
  }, [scoped, filters]);

  
  const visible = useMemo(() => {
    if (!sameStartOnly) return filtered;
    const countAll = {};
    rows.forEach((r) => { if (r.start) countAll[r.start] = (countAll[r.start] || 0) + 1; });
    const keep = new Set(filtered.map((r) => r.start).filter((d) => d && countAll[d] > 1));
    return rows.filter((r) => keep.has(r.start));
  }, [filtered, sameStartOnly, rows]);

  
  const ordered = useMemo(() =>
    [...visible].sort((a, b) => (a.start || "").localeCompare(b.start || "") ||
      String(a["Site"] || "").localeCompare(String(b["Site"] || ""))), [visible]);

  const summary = useMemo(() => ({
    totalPeriodicals: scoped.length,
    startsToday: scoped.filter(startsToday).length,
    counts: counts.c,
    money: counts.money,
  }), [scoped, counts]);

  const filterHeader = useMemo(() => {
    const parts = [`${MONTHS3[month]} ${year}`];
    if (todayOnly) parts.push("Starts today only");
    if (sameStartOnly) parts.push("Shared start dates only");
    if (dates.length) parts.push(`Start Dates: ${dates.map(fmtDate).join(", ")}`);
    if (query.trim()) parts.push(`Search: "${query.trim()}"`);
    const act = FILTERS.filter((x) => filters.includes(x.key)).map((x) => x.label);
    parts.push(act.length ? `Filter: ${act.join(" + ")}` : "Filter: All periodicals");
    return parts.join(" · ");
  }, [month, year, todayOnly, sameStartOnly, dates, query, filters]);

  
  const reportRows = useMemo(() => {
    const active = FILTERS.filter((f) => filters.includes(f.key));
    return visible.map((r) => ({
      "Start Date": r.start, "End Date": r.end, "Periodical": r["Periodical"], "Site": r["Site"],
      "Cycle": r["Cycle-Type1"], "Work": isABN(r) ? "Contractor" : isTFN(r) ? "Employee" : r["Work1"], "Work Status": r["Work Status"],
      "Cleaners": r.cleaners, "Cleaner Attending": isClaimed(r) ? r["Cleaner Attending"] : "Unclaimed",
      "Quote": r.quote, "SNL Payment": r.snl, "Cleaner Payment": r.payEach, "Cleaners Payment Total": r.payTotal,
      "Days": r.days, "Rollover": r["Rollover"], "Starts Today": startsToday(r) ? "Yes" : "No",
      "Matched": active.filter((f) => f.test(r)).map((f) => f.label),
    }));
  }, [visible, filters]);

  const shell = (
    <>
      <style>{`@keyframes pd-spin{to{transform:rotate(360deg)}}
        `}</style>
      <div style={{ ...S.modal, margin: inline ? "0 auto" : undefined }} onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
          <h4 style={S.title}>Periodical Dashboard</h4>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <HelpButton helpData={[
              { chip: 'periodicaldashboard', urlFilter: '/static/pdffilters/PeriodicalDashboard.pdf' }
            ]} />
            <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
              title="Refresh data — keeps current filters" onClick={refresh}>
              {refreshing ? <Spin /> : "↻"}
            </button>
            <button type="button" style={S.navBtn} title="Quick filter — claimed in advance of the start date"
              onClick={advancePreset}>⚡</button>
            <button type="button" style={S.navBtn} title="Send report for the current filter"
              onClick={() => setSendOpen(true)}>✉</button>
            {!inline && (
              <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
            )}
          </div>
        </div>

        {/* month navigation */}
        <div style={S.nav}>
          <button type="button" style={S.navBtn} onClick={() => nav(-1)}>◄</button>
          <span style={S.navLabel}>{MONTHS3[month]} {year}</span>
          <button type="button" style={S.navBtn} onClick={() => nav(1)}>►</button>
        </div>

        <SearchInput value={query} onChange={setQuery} placeholder="Search site, periodical, status or work…" />

        
        <div style={S.chipsRow}>
          <button type="button" style={S.dayChip(todayOnly)} title="Periodicals starting today"
            onClick={() => setTodayOnly((p) => !p)}>
            ⚡ Starts Today
          </button>
          <button type="button" style={S.dayChip(sameStartOnly)} title="Periodicals that share a start date with another"
            onClick={() => setSameStartOnly((p) => !p)}>
            <span style={{ color:"#f97316", fontWeight:900 }}>⧉</span> Same Start Date
          </button>
          <button type="button" style={S.dayChip(dates.length > 0)} title="Filter specific Start Dates"
            onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.click()}>
            📅
          </button>
          <input ref={dateRef} type="date" min={dateMin} max={dateMax}
            style={{ position:"absolute", opacity:0, pointerEvents:"none", width:0, height:0 }}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setDates((p) => (p.includes(v) ? p : [...p, v]));
              e.target.value = "";
            }} />
          {dates.map((d) => (
            <button key={d} type="button" style={S.dayChip(true)} title="Remove"
              onClick={() => setDates((p) => p.filter((x) => x !== d))}>
              {fmtDate(d)} ×
            </button>
          ))}
        </div>

        
        {ROWS_OF_FILTERS.map((row, i) => (
          <div key={i} style={{ ...S.buttonsGrid, margin: i === 0 ? "10px 0 10px" : i === 1 ? "0 0 10px" : "0 0 6px" }}>
            {row.map((f) => (
              <button key={f.key} type="button" style={S.countBtn(filters.includes(f.key))}
                onClick={() => setFilters((p) => p.includes(f.key) ? p.filter((x) => x !== f.key) : [...p, f.key])}>
                <span style={S.countLabel}>{f.label}</span>
                <span style={S.countValue(f.tone, f.money)}>
                  {loading ? "…" : f.money ? fmt$(counts.money[f.key]) : counts.c[f.key]}
                </span>
                {f.money && !loading && <span style={S.countSub}>{counts.c[f.key]} periodical{counts.c[f.key] === 1 ? "" : "s"}</span>}
                {f.sub && !loading && counts.sub[f.key] > 0 && (
                  <span style={S.countSub}>⚡ {counts.sub[f.key]} start today</span>
                )}
              </button>
            ))}
          </div>
        ))}

        
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading {MONTHS3[month]} {year}…</div>
          ) : ordered.length === 0 ? (
            <div style={S.empty}>No periodicals for this view.</div>
          ) : (
            <div style={{ maxHeight:460, overflowY:"auto", overflowX:"auto" }}>
              <div style={{ minWidth:820 }}>
                {ordered.map((r, i) => <PeriodicalRow key={r.id || i} r={r} />)}
              </div>
            </div>
          )}
        </div>

        {!inline && <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>}
        {sendOpen && (
          <SendModal filterHeader={filterHeader} reportRows={reportRows} summary={summary} onClose={() => setSendOpen(false)} />
        )}
      </div>
    </>
  );
  return inline ? shell : <div style={S.backdrop} onClick={onClose}>{shell}</div>;
};

export default PeriodicalDashboard;